import type { Vec2 } from "../types";

export interface SteerConfig {
  /** Gaze magnitude below this yields zero velocity. */
  deadzone: number;
  /** px/s at full deflection. */
  maxSpeed: number;
  /** Power curve applied to the post-deadzone magnitude (>1 = softer near centre). */
  exponent: number;
}

const DEFAULT_STEER_CONFIG: SteerConfig = {
  deadzone: 0.08,
  maxSpeed: 520,
  exponent: 1.6,
};

/** Radial deadzone + power curve + max speed. `gaze` axes are expected in -1..1. */
export function steerFromGaze(gaze: Vec2, cfg: Partial<SteerConfig> = {}): Vec2 {
  const { deadzone, maxSpeed, exponent } = { ...DEFAULT_STEER_CONFIG, ...cfg };
  const mag = Math.hypot(gaze.x, gaze.y);
  if (mag <= deadzone || mag === 0) {
    return { x: 0, y: 0 };
  }

  const clamped = Math.min(mag, 1);
  const normalized = (clamped - deadzone) / (1 - deadzone);
  const curved = Math.max(0, normalized) ** exponent;
  const speed = curved * maxSpeed;

  const dirX = gaze.x / mag;
  const dirY = gaze.y / mag;
  return { x: dirX * speed, y: dirY * speed };
}

/** Integrates `v` (px/s) into `pos` over `dtS` seconds, clamped to `[0, bounds.w] x [0, bounds.h]`. */
export function applyVelocity(
  pos: Vec2,
  v: Vec2,
  dtS: number,
  bounds: { w: number; h: number },
): Vec2 {
  const x = pos.x + v.x * dtS;
  const y = pos.y + v.y * dtS;
  return {
    x: Math.max(0, Math.min(bounds.w, x)),
    y: Math.max(0, Math.min(bounds.h, y)),
  };
}
